import { Observable } from 'rxjs';
import axios from 'axios';
import ssoService from '@/services/ssoService';
import AxiosSubscriber from './AxiosSubscriber';

const instance = axios.create({
  baseURL: JSON.parse(process.env.VUE_APP_USE_ENV) ? '${VUE_APP_APIPATH_GATEWAY}' : process.env.VUE_APP_APIPATH_GATEWAY,
});

const ACCESS_TOKEN_KEY = '_gateteway_t';
const limit = 10;

const getAccessToken = function getAccessToken() {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

const setAccessToken = function setAccessToken(token) {
  return sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
};

// 取新token
const postAPIToken = async function postAPIToken() {
  return ssoService.postAPIToken().then((res) => {
    const data = res.response;

    if (data.success) {
      setAccessToken(data.data[0].apiString);
    }

    return res;
  });
};

// 取token
const getAPIToken = async function getAPIToken() {
  return ssoService.getAPIToken().then((res) => {
    const data = res.response;

    if (data.success) {
      setAccessToken(data.data[0].apiString);

      return res;
    }

    return postAPIToken();
  }).catch(() => postAPIToken());
};

// default headers
instance.interceptors.request.use(async(config) => {
  let accessToken = getAccessToken();

  if (!accessToken) {
    await getAPIToken();

    accessToken = getAccessToken();
  }

  config.headers = Object.assign(config.headers, {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  });

  return config;
});

instance.interceptors.response.use((response) => response, async(error) => {
  const resp = error.response;
  if (resp.status === 401) {
    const newConfig = { ...error.config };
    const count = newConfig.retryCount || 0;
    newConfig.retryCount = count + 1;

    await postAPIToken();

    if (newConfig.retryCount < limit) {
      // 成功取得新 access token
      const waitTime = count ** 2 * 100;
      return new Promise((resolve, reject) => {
        // 指數式重試
        setTimeout(() => {
          const accessToken = getAccessToken();
          newConfig.headers = Object.assign(newConfig.headers, {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          });

          instance.request(newConfig).then((response) => {
            resolve(response);
          }).catch((err) => {
            reject(err);
          });
        }, waitTime);
      });
    }
    throw error;
  }

  throw error;
});

const getQuery = function getQuery(params) {
  return Object.keys(params).map((key) => `${key}=${params[key]}`).join('&');
};
const transformResponse = {
  success(res) {
    return { response: res.data, status: res.status };
  },
  error(err) {
    return err.response;
  },
};

export default {
  get(source, params, headers, config) {
    const query = params ? `?${getQuery(params)}` : '';

    return new Observable((observer) => {
      let baseConfig = {
        method: 'get',
        url: `${source}${query}`,
        headers,
        customResponse: transformResponse,
      };

      if (config) baseConfig = Object.assign(baseConfig, config);
      return new AxiosSubscriber(observer, baseConfig, instance);
    });
  },

  post(source, data, headers, config) {
    return new Observable((observer) => {
      let baseConfig = {
        method: 'post',
        url: source,
        data: data || {},
        headers,
        customResponse: transformResponse,
      };

      if (config) baseConfig = Object.assign(baseConfig, config);
      return new AxiosSubscriber(observer, baseConfig, instance);
    });
  },

  patch(source, data, headers, config) {
    return new Observable((observer) => {
      let baseConfig = {
        method: 'patch',
        url: source,
        data: data || {},
        headers,
        customResponse: transformResponse,
      };

      if (config) baseConfig = Object.assign(baseConfig, config);
      return new AxiosSubscriber(observer, baseConfig, instance);
    });
  },

  put(source, data, headers, config) {
    return new Observable((observer) => {
      let baseConfig = {
        method: 'put',
        url: source,
        data: data || {},
        headers,
        customResponse: transformResponse,
      };

      if (config) baseConfig = Object.assign(baseConfig, config);
      return new AxiosSubscriber(observer, baseConfig, instance);
    });
  },

  delete(source, target, headers, config) {
    return new Observable((observer) => {
      let baseConfig = {
        method: 'delete',
        url: `${source}/${target}`,
        headers,
        customResponse: transformResponse,
      };

      if (config) baseConfig = Object.assign(baseConfig, config);
      return new AxiosSubscriber(observer, baseConfig, instance);
    });
  },
};
